import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import DraftControls from '../components/DraftControls';
import { AvatarUpload } from '../components/AvatarUpload';
import MergeDuplicatesModal from '../components/MergeDuplicatesModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Upload, 
  ChevronUp, ChevronDown, User, Search, RefreshCw, Star, GitMerge
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = ['Invited', 'Schedule Permitting', 'Confirmed'];
const tierOptions = [
  { value: 1, label: 'Tier 1 - Government Leadership' },
  { value: 2, label: 'Tier 2 - Congressional Leadership' },
  { value: 3, label: 'Tier 3 - Agency & Regulatory' },
  { value: 4, label: 'Tier 4 - Industry Leadership' },
  { value: 5, label: 'Tier 5 - Experts & Academics' }
];

const AdminSpeakers = () => {
  const navigate = useNavigate();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // 'all' | 'visible' | 'hidden'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [newSpeaker, setNewSpeaker] = useState({
    name: '',
    title: '',
    organization: '',
    image_url: '',
    bio: 'Bio coming soon.',
    status: 'Invited',
    tier: 5,
    visible: true,
    is_featured: false
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSpeakers();
  }, [navigate]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/speakers?visible_only=false`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch speakers');
      const data = await response.json();
      setSpeakers(data);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load speakers');
    } finally {
      setLoading(false);
    }
  };

  const seedSpeakers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/speakers/seed-initial`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchSpeakers();
      }
    } catch (err) {
      toast.error('Failed to seed speakers');
    }
  };

  const handleSave = async (speaker) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/speakers/${speaker.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(speaker)
      });
      
      if (!response.ok) throw new Error('Failed to update speaker');
      
      toast.success('Speaker updated successfully');
      setEditingSpeaker(null);
      fetchSpeakers();
    } catch (err) {
      toast.error('Failed to update speaker');
    }
  };

  const handleDelete = async (speakerId) => {
    if (!window.confirm('Are you sure you want to delete this speaker?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/speakers/${speakerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete speaker');
      
      toast.success('Speaker deleted');
      fetchSpeakers();
    } catch (err) {
      toast.error('Failed to delete speaker');
    }
  };

  const handleAddSpeaker = async () => {
    if (!newSpeaker.name || !newSpeaker.title || !newSpeaker.organization) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/speakers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSpeaker)
      });
      
      if (!response.ok) throw new Error('Failed to add speaker');
      
      toast.success('Speaker added successfully');
      setShowAddForm(false);
      setNewSpeaker({
        name: '',
        title: '',
        organization: '',
        image_url: '',
        bio: 'Bio coming soon.',
        status: 'Invited',
        tier: 5,
        visible: true,
        is_featured: false
      });
      fetchSpeakers();
    } catch (err) {
      toast.error('Failed to add speaker');
    }
  };

  const toggleVisibility = async (speaker) => {
    await handleSave({ ...speaker, visible: !speaker.visible });
  };

  const toggleFeatured = async (speaker) => {
    // Allow up to 6 featured speakers
    const currentFeaturedCount = speakers.filter(s => s.is_featured).length;
    
    if (!speaker.is_featured && currentFeaturedCount >= 6) {
      toast.error('Maximum 6 featured speakers allowed. Remove one first.');
      return;
    }
    
    await handleSave({ ...speaker, is_featured: !speaker.is_featured });
  };

  const toggleRowSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkSetVisibility = async (visible) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const results = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/api/speakers/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ visible })
        }).then(r => r.ok).catch(() => false)
      ));
      const okCount = results.filter(Boolean).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(`${visible ? 'Showed' : 'Hid'} ${okCount} speaker${okCount === 1 ? '' : 's'}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} speaker${failCount === 1 ? '' : 's'}`);
      }
      setSelectedIds(new Set());
      await fetchSpeakers();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetTier = async (tier) => {
    if (selectedIds.size === 0 || !tier) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const results = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/api/speakers/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ tier })
        }).then(r => r.ok).catch(() => false)
      ));
      const okCount = results.filter(Boolean).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(`Set ${okCount} speaker${okCount === 1 ? '' : 's'} to Tier ${tier}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} speaker${failCount === 1 ? '' : 's'}`);
      }
      setSelectedIds(new Set());
      await fetchSpeakers();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetStatus = async (status) => {
    if (selectedIds.size === 0 || !status) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const results = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/api/speakers/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status })
        }).then(r => r.ok).catch(() => false)
      ));
      const okCount = results.filter(Boolean).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(`Set ${okCount} speaker${okCount === 1 ? '' : 's'} to "${status}"`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} speaker${failCount === 1 ? '' : 's'}`);
      }
      setSelectedIds(new Set());
      await fetchSpeakers();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetFeatured = async (is_featured) => {
    if (selectedIds.size === 0) return;
    const selected = speakers.filter(s => selectedIds.has(s.id));
    let targets = selected;
    let skipped = 0;

    if (is_featured) {
      const alreadyFeaturedInSelection = selected.filter(s => s.is_featured);
      const toFeature = selected.filter(s => !s.is_featured);
      const currentFeaturedCount = speakers.filter(s => s.is_featured).length;
      const slotsLeft = Math.max(0, 6 - currentFeaturedCount);
      if (toFeature.length > slotsLeft) {
        skipped = toFeature.length - slotsLeft;
        targets = [...alreadyFeaturedInSelection, ...toFeature.slice(0, slotsLeft)];
        if (slotsLeft === 0) {
          toast.error('Maximum 6 featured speakers reached. Remove some first.');
          return;
        }
      }
    }

    const ids = targets.map(s => s.id);
    if (ids.length === 0) {
      toast.error('No changes to apply.');
      return;
    }
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const results = await Promise.all(ids.map(id =>
        fetch(`${API_URL}/api/speakers/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ is_featured })
        }).then(r => r.ok).catch(() => false)
      ));
      const okCount = results.filter(Boolean).length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(`${is_featured ? 'Featured' : 'Unfeatured'} ${okCount} speaker${okCount === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} skipped — 6-max cap)` : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} speaker${failCount === 1 ? '' : 's'}`);
      }
      setSelectedIds(new Set());
      await fetchSpeakers();
    } finally {
      setBulkBusy(false);
    }
  };

  const filteredSpeakers = speakers.filter(s => {
    const matchesSearch = (
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.organization.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (!matchesSearch) return false;
    if (visibilityFilter === 'visible') return s.visible;
    if (visibilityFilter === 'hidden') return !s.visible;
    return true;
  });

  const hiddenCount = speakers.filter(s => !s.visible).length;

  const getStatusBadge = (status) => {
    const colors = {
      'Confirmed': 'bg-green-100 text-green-800',
      'Schedule Permitting': 'bg-yellow-100 text-yellow-800',
      'Invited': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-4">
          <DraftControls draftKey="speakers" pageLabel="Speakers" previewPath="/speakers" />
        </div>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Speaker Management</h1>
            <p className="text-gray-600">Manage event speakers and their information</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowMergeModal(true)}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="find-duplicates-btn"
            >
              <GitMerge className="w-4 h-4" />
              Find Duplicates
            </Button>
            <Button
              onClick={seedSpeakers}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Seed Initial Data
            </Button>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-[#00457f] hover:bg-[#003461] flex items-center gap-2"
              data-testid="add-speaker-btn"
            >
              <Plus className="w-4 h-4" />
              Add Speaker
            </Button>
          </div>
        </div>

        <MergeDuplicatesModal
          open={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          onMerged={fetchSpeakers}
        />

        {/* Search + Visibility Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search speakers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f] focus:border-transparent"
            />
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden shrink-0" role="tablist" aria-label="Filter by visibility">
            {[
              { key: 'all', label: `All (${speakers.length})` },
              { key: 'visible', label: `Visible (${speakers.length - hiddenCount})` },
              { key: 'hidden', label: `Hidden (${hiddenCount})` },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={visibilityFilter === opt.key}
                data-testid={`speaker-filter-${opt.key}`}
                onClick={() => setVisibilityFilter(opt.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  visibilityFilter === opt.key
                    ? 'bg-[#00457f] text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Add Speaker Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-2 border-[#00457f]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#00457f]">Add New Speaker</h2>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newSpeaker.name}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newSpeaker.title}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  placeholder="Job title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
                <input
                  type="text"
                  value={newSpeaker.organization}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, organization: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  placeholder="Company/Organization"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                <AvatarUpload
                  value={newSpeaker.image_url}
                  onChange={(url) => setNewSpeaker({ ...newSpeaker, image_url: url })}
                  folder="speakers"
                  shape="circle"
                  testId="new-speaker-image"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newSpeaker.status}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                >
                  {statusOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                <select
                  value={newSpeaker.tier}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, tier: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                >
                  {tierOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={newSpeaker.bio}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, bio: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                  placeholder="Speaker biography..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={handleAddSpeaker} className="bg-[#00457f] hover:bg-[#003461]">
                <Save className="w-4 h-4 mr-2" />
                Add Speaker
              </Button>
            </div>
          </div>
        )}

        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div
            data-testid="speaker-bulk-actions-bar"
            className="mb-4 flex flex-wrap items-center gap-3 px-4 py-3 bg-[#eef4fb] border border-[#00457f]/20 rounded-lg"
          >
            <span className="text-sm font-medium text-[#00457f]">
              {selectedIds.size} selected
            </span>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap">Set tier:</label>
              <select
                data-testid="speaker-bulk-tier"
                disabled={bulkBusy}
                value=""
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) bulkSetTier(v);
                  e.target.value = '';
                }}
                className="text-sm px-2 py-1.5 border rounded-md bg-white focus:ring-2 focus:ring-[#00457f]"
              >
                <option value="">Choose…</option>
                {tierOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap">Set status:</label>
              <select
                data-testid="speaker-bulk-status"
                disabled={bulkBusy}
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) bulkSetStatus(v);
                  e.target.value = '';
                }}
                className="text-sm px-2 py-1.5 border rounded-md bg-white focus:ring-2 focus:ring-[#00457f]"
              >
                <option value="">Choose…</option>
                {statusOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <Button
              data-testid="speaker-bulk-feature"
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              onClick={() => bulkSetFeatured(true)}
              className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
            >
              <Star className="w-4 h-4 mr-1" /> Feature
            </Button>
            <Button
              data-testid="speaker-bulk-unfeature"
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              onClick={() => bulkSetFeatured(false)}
              className="border-gray-400 text-gray-700 hover:bg-gray-100"
            >
              <Star className="w-4 h-4 mr-1" /> Unfeature
            </Button>
            <Button
              data-testid="speaker-bulk-show"
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              onClick={() => bulkSetVisibility(true)}
              className="border-green-600 text-green-700 hover:bg-green-50"
            >
              <Eye className="w-4 h-4 mr-1" /> Show
            </Button>
            <Button
              data-testid="speaker-bulk-hide"
              variant="outline"
              size="sm"
              disabled={bulkBusy}
              onClick={() => bulkSetVisibility(false)}
              className="border-gray-500 text-gray-700 hover:bg-gray-100"
            >
              <EyeOff className="w-4 h-4 mr-1" /> Hide
            </Button>
            <button
              data-testid="speaker-bulk-clear"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
              disabled={bulkBusy}
            >
              Clear
            </button>
          </div>
        )}

        {/* Speakers List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#00457f] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        data-testid="speaker-select-all"
                        aria-label="Select all filtered speakers"
                        className="w-4 h-4 text-[#00457f] focus:ring-[#00457f] border-gray-300 rounded cursor-pointer"
                        checked={
                          filteredSpeakers.length > 0 &&
                          filteredSpeakers.every(s => selectedIds.has(s.id))
                        }
                        ref={el => {
                          if (el) {
                            const anySel = filteredSpeakers.some(s => selectedIds.has(s.id));
                            const allSel = filteredSpeakers.length > 0 && filteredSpeakers.every(s => selectedIds.has(s.id));
                            el.indeterminate = anySel && !allSel;
                          }
                        }}
                        onChange={(e) => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) {
                              filteredSpeakers.forEach(s => next.add(s.id));
                            } else {
                              filteredSpeakers.forEach(s => next.delete(s.id));
                            }
                            return next;
                          });
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Speaker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title / Org</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Featured</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visible</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSpeakers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No speakers found. Click &quot;Seed Initial Data&quot; to add the initial speaker list.
                      </td>
                    </tr>
                  ) : (
                    filteredSpeakers.map((speaker) => (
                      <tr key={speaker.id} className={!speaker.visible ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-4 whitespace-nowrap w-10">
                          <input
                            type="checkbox"
                            data-testid={`speaker-select-${speaker.id}`}
                            aria-label={`Select ${speaker.name}`}
                            className="w-4 h-4 text-[#00457f] focus:ring-[#00457f] border-gray-300 rounded cursor-pointer"
                            checked={selectedIds.has(speaker.id)}
                            onChange={() => toggleRowSelected(speaker.id)}
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {speaker.image_url ? (
                                <img 
                                  className="h-10 w-10 rounded-full object-cover" 
                                  src={speaker.image_url} 
                                  alt={speaker.name}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="w-5 h-5 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                {speaker.name}
                                {!speaker.visible && (
                                  <span
                                    data-testid={`speaker-hidden-badge-${speaker.id}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-gray-200 text-gray-700"
                                  >
                                    <EyeOff className="w-3 h-3" />
                                    Hidden
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{speaker.title}</div>
                          <div className="text-sm text-gray-500">{speaker.organization}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(speaker.status)}`}>
                            {speaker.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          Tier {speaker.tier}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleFeatured(speaker)}
                            className={`p-1 rounded ${speaker.is_featured ? 'text-yellow-500 hover:bg-yellow-50' : 'text-gray-300 hover:bg-gray-100 hover:text-yellow-500'}`}
                            title={speaker.is_featured ? 'Remove from spotlight' : 'Set as featured speaker'}
                          >
                            <Star className={`w-5 h-5 ${speaker.is_featured ? 'fill-current' : ''}`} />
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            data-testid={`speaker-toggle-visibility-${speaker.id}`}
                            onClick={() => toggleVisibility(speaker)}
                            className={`p-1 rounded ${speaker.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                            title={speaker.visible ? 'Hide from public page' : 'Show on public page'}
                          >
                            {speaker.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => setEditingSpeaker(speaker)}
                            className="text-[#00457f] hover:text-[#003461] p-1"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(speaker.id)}
                            className="text-red-600 hover:text-red-800 p-1 ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingSpeaker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold text-[#00457f]">Edit Speaker</h2>
                  <button onClick={() => setEditingSpeaker(null)} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={editingSpeaker.name}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingSpeaker.title}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, title: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                    <input
                      type="text"
                      value={editingSpeaker.organization}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, organization: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                    <AvatarUpload
                      value={editingSpeaker.image_url}
                      onChange={(url) => setEditingSpeaker({ ...editingSpeaker, image_url: url })}
                      folder="speakers"
                      shape="circle"
                      testId="edit-speaker-image"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editingSpeaker.status}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, status: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    >
                      {statusOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                    <select
                      value={editingSpeaker.tier}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, tier: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    >
                      {tierOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Override</label>
                    <input
                      type="number"
                      value={editingSpeaker.order_override || ''}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, order_override: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                      placeholder="Leave empty for auto-sort"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingSpeaker.visible}
                        onChange={(e) => setEditingSpeaker({ ...editingSpeaker, visible: e.target.checked })}
                        className="w-4 h-4 text-[#00457f] focus:ring-[#00457f] border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Visible on public page</span>
                    </label>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingSpeaker.is_featured || false}
                        onChange={(e) => setEditingSpeaker({ ...editingSpeaker, is_featured: e.target.checked })}
                        className="w-4 h-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                      />
                      <Star className={`ml-2 w-4 h-4 ${editingSpeaker.is_featured ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
                      <span className="ml-1 text-sm text-gray-700">Featured Speaker (Spotlight)</span>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={editingSpeaker.bio}
                      onChange={(e) => setEditingSpeaker({ ...editingSpeaker, bio: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00457f]"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setEditingSpeaker(null)}>Cancel</Button>
                  <Button 
                    onClick={() => handleSave(editingSpeaker)} 
                    className="bg-[#00457f] hover:bg-[#003461]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSpeakers;
